import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import {
  TextField as RATextField,
  FunctionField as RAFunctionField,
  sanitizeFetchType,
  SimpleShowLayout,
  SelectInput,
  useListContext,
  useMutation,
  useNotify,
  useTranslate,
  DateField,
  NumberField,
  SimpleForm,
  ToolbarProps,
  Toolbar,
  SaveButton,
  useQuery,
} from 'react-admin'
import { ResourceName } from '../../../core/ResourceName'
import { ConfirmDialog } from '../../common/ConfirmDialog'
import Button from '../../common/customized-mui-components/Button'
import DraggableComponent from '../../common/DraggableComponent'
import {
  CancelRequest,
  DiscountRequest,
  GetRecordRequest,
} from '../../../core/external-parking/external-parking.provider'
import { ParkingRecordDto } from '../../../core/dto/external-parking/parking-record.dto'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogProperty: {
      marginBottom: theme.spacing(1),
    },
    bulkStateActions: {
      height: '36px',
      background: 'rgba(0,0,0,0.2)',
      // minWidth: '300px',
      userSelect: 'none',
    },
    bulkProgressTitle: {
      paddingLeft: '10px',
      width: '100%',
    },
    bulkProgressCloseButton: {
      margin: 0,
    },
    actionsButton: {
      margin: '2%',
      width: '96%',
    },
    actionsDoubleButtons: {
      margin: '2%',
      width: '46%',
    },
    dialogContent: {
      padding: '8px',
    },
  }),
)

export const CheckExternalParkingTicketDialog = ({
  open,
  close,
}: {
  open: boolean
  close: () => void
}) => {
  const { filterValues } = useListContext()
  const classes = useStyles()
  const translate = useTranslate()
  const notify = useNotify()
  const [mutate] = useMutation()
  const [ticketCode, setTicketCode] = useState<string>('')
  const [ticketCodeInputOpened, setTicketCodeInputOpen] = useState(true)
  const [ticketInfoOpened, setTicketInfoOpen] = useState(false)
  const [discountPicker, setDiscountPicker] = useState<boolean>(false)
  const [cancelTicketConfirmationOpened, setCancelTicketConfirmationOpen] =
    useState(false)
  const handleEnter = () => {
    setTicketCode(filterValues.ticketCode ?? '')
  }
  const handleClose = () => {
    setTicketCode('')
    setTicketCodeInputOpen(true)
    setTicketInfoOpen(false)
    setDiscountPicker(false)
    setCancelTicketConfirmationOpen(false)
    close()
  }
  const handleCloseDiscountForm = () => {
    setDiscountPicker(false)
  }
  const [getTicket, { data: ticketData, loading: ticketDataLoading }] =
    useMutation(
      {
        type: sanitizeFetchType('getRecord'),
        resource: ResourceName.EXTERNAL_PARKING,
        payload: {
          ticketCode,
        } as GetRecordRequest,
      },
      {
        returnPromise: true,
        onFailure: () =>
          notify(
            translate(
              'resources.external-parking.dialogs.checkParkingTicket.results.getTicket.error',
            ),
            'error',
          ),
      },
    )
  const [cancelTicket] = useMutation(
    {
      type: sanitizeFetchType('cancel'),
      resource: ResourceName.EXTERNAL_PARKING,
      payload: {
        ticketCode: (ticketData as ParkingRecordDto)?.barcode,
        updateCounters: false,
      } as CancelRequest,
    },
    {
      returnPromise: true,
      onFailure: () =>
        notify(
          translate(
            'resources.external-parking.dialogs.checkParkingTicket.results.cancel.error',
          ),
          'error',
        ),
      onSuccess: () => {
        notify(
          translate(
            'resources.external-parking.dialogs.checkParkingTicket.results.cancel.success',
          ),
          'success',
        )
        handleClose()
      },
    },
  )

  const { data: discountsList } = useQuery(
    {
      type: sanitizeFetchType('getDiscounts'),
      resource: ResourceName.EXTERNAL_PARKING,
      payload: {},
    },
    { enabled: discountPicker },
  )
  const discountTicket = async (discountId: number) => {
    await mutate(
      {
        type: sanitizeFetchType('discount'),
        resource: ResourceName.EXTERNAL_PARKING,
        payload: {
          ticketCode: (ticketData as ParkingRecordDto)?.barcode,
          discountId,
        } as DiscountRequest,
      },
      {
        returnPromise: true,
        onFailure: () =>
          notify(
            translate(
              'resources.external-parking.dialogs.checkParkingTicket.results.discount.error',
            ),
            'error',
          ),
        onSuccess: () => {
          notify(
            translate(
              'resources.external-parking.dialogs.checkParkingTicket.results.discount.success',
            ),
            'success',
          )
          handleCloseDiscountForm()
        },
      },
    )
  }

  // Show ticket info when ticket found
  useEffect(() => {
    if (ticketData && !ticketDataLoading) {
      setTicketInfoOpen(true)
      setTicketCodeInputOpen(false)
    }
  }, [ticketData, ticketDataLoading, setTicketInfoOpen])

  const DiscountFormActions = (props: ToolbarProps) => (
    <Toolbar {...props}>
      <SaveButton
        variant="contained"
        size="small"
        className={classes.actionsButton}
        onSave={(values) => {
          discountTicket((values as any)?.discountId ?? null)
        }}
      />
      <Button
        variant="contained"
        onClick={() => setDiscountPicker(false)}
        label={translate('const.cancel')}
        useSmallVersionBreakpoint={false}
        className={classes.actionsButton}
      />
    </Toolbar>
  )

  return (
    <Dialog
      open={open}
      onEnter={handleEnter}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={cancelTicketConfirmationOpened}
        cancel={() => setCancelTicketConfirmationOpen(false)}
        confirm={cancelTicket}
        source="cancelTicket"
        action="set"
        resource={ResourceName.EXTERNAL_PARKING}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(
            'resources.external-parking.dialogs.searchForTicket.title',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      {ticketCodeInputOpened && (
        <>
          <DialogContent>
            <TextField
              className={classes.dialogProperty}
              label={translate(
                'resources.external-parking.dialogs.searchForTicket.ticketCode.label',
              )}
              variant="outlined"
              value={ticketCode}
              onChange={(e) => setTicketCode(e.target.value)}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={getTicket}
              label={translate(
                'resources.external-parking.dialogs.searchForTicket.actions.getTicket',
              )}
              useSmallVersionBreakpoint={false}
              className={classes.actionsButton}
              disabled={!ticketCode}
            />
          </DialogActions>
        </>
      )}
      {ticketInfoOpened && (
        <>
          <DialogContent>
            {ticketData?.id ? (
              <SimpleShowLayout
                record={ticketData as ParkingRecordDto}
                resource={ResourceName.EXTERNAL_PARKING}
              >
                <Typography variant="h5" paragraph>
                  {translate(
                    'resources.external-parking.dialogs.searchForTicket.ticketFound',
                  )}
                </Typography>
                <RATextField source="id" />
                <RATextField source="barcode" />
                {ticketData?.plateNumber && (
                  <RATextField source="plateNumber" />
                )}
                <RAFunctionField<ParkingRecordDto>
                  source="state"
                  render={(record?: ParkingRecordDto) => (
                    <div>
                      {translate(
                        `resources.enums.ParkingRecord.${record?.state}`,
                      )}
                    </div>
                  )}
                />
                <DateField source="enteredAt" showTime />
                <DateField source="exitedAt" showTime />
                <NumberField
                  source="payedAmount"
                  options={{ style: 'currency', currency: 'PLN' }}
                />
              </SimpleShowLayout>
            ) : (
              <>
                <Typography
                  variant="h4"
                  paragraph
                  style={{
                    marginTop: 16,
                    marginBottom: 16,
                    textAlign: 'center',
                  }}
                >
                  {translate(
                    'resources.external-parking.dialogs.searchForTicket.ticketNotFound',
                  )}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setCancelTicketConfirmationOpen(true)}
              label={translate(
                'resources.external-parking.dialogs.searchForTicket.actions.cancelTicket',
              )}
              useSmallVersionBreakpoint={false}
              className={classes.actionsDoubleButtons}
              disabled={!(ticketData as ParkingRecordDto)?.barcode}
            />
            <Button
              variant="contained"
              onClick={() => setDiscountPicker(true)}
              color="primary"
              className={classes.actionsDoubleButtons}
              label="resources.external-parking.dialogs.checkParkingTicket.actions.discountTicket"
              useSmallVersionBreakpoint={false}
            />
            {}
          </DialogActions>
        </>
      )}
      <Dialog
        open={discountPicker}
        onClose={() => setDiscountPicker(false)}
        disableBackdropClick
        disableEscapeKeyDown
        PaperComponent={DraggableComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <SimpleForm
          toolbar={<DiscountFormActions />}
          resource={ResourceName.EXTERNAL_PARKING}
        >
          <SelectInput source="discountId" choices={discountsList} />
        </SimpleForm>
      </Dialog>
    </Dialog>
  )
}
